<template>
  <i18n-t
    v-if="validateRecaptcha && visible"
    class="g-recaptcha-notice"
    :class="[
      'g-recaptcha-notice',
      { 'g-recaptcha-notice--no-margin': noMargin },
      { 'g-recaptcha-notice--small-print': smallPrint },
    ]"
    keypath="recaptcha.notice"
    tag="p"
    scope="global"
  >
    <template #privacy>
      <fn-link
        inline
        :href="$t('recaptcha.privacyUrl')"
        target="_blank"
      >
        {{ $t('recaptcha.privacy') }}
      </fn-link>
    </template>
    <template #terms>
      <fn-link
        inline
        :href="$t('recaptcha.termsUrl')"
        target="_blank"
      >
        {{ $t('recaptcha.terms') }}
      </fn-link>
    </template>
  </i18n-t>
</template>

<script setup lang="ts">
import { actions } from '@/utils/recaptcha';

type RecaptchaExecuteResult = { recaptchaResponse?: string; validateRecaptcha?: boolean };

const props = withDefaults(
  defineProps<{
    validateRecaptcha?: boolean;
    visible?: boolean;
    noMargin?: boolean;
    smallPrint?: boolean;
  }>(),
  {
    validateRecaptcha: true,
    visible: true,
  },
);

const emit = defineEmits(['verify']);

const { $config, $log } = useNuxtApp();
const mainStore = useMainStore();

const recaptchaSiteKey = computed(() => mainStore.configuration?.recaptchaSiteKey);

function executeScript(
  action: String<string>,
  recaptchaSiteKey: String<string>,
  formFieldValues: Object<Object>,
  resolve: (value: RecaptchaExecuteResult) => void,
) {
  try {
    window.grecaptcha.enterprise.ready(() => {
      window.grecaptcha.enterprise.execute(unref(recaptchaSiteKey), { action }).then((token) => {
        const payload = {
          ...(formFieldValues.values || formFieldValues),
          recaptchaResponse: token,
          validateRecaptcha: props.validateRecaptcha,
        };
        emit('verify', payload);
        resolve(payload);
      });
    });
  } catch (error) {
    $log.error('Error in recaptcha execute():', error);
    resolve({});
  }
}

defineExpose({
  execute: (action = actions.submit, formFieldValues: Record<string, any> = {}) => {
    return new Promise<RecaptchaExecuteResult>((resolve) => {
      if (!$config.public.googleRecaptchaScript || !unref(recaptchaSiteKey)) {
        resolve({});
        return;
      }

      if (import.meta.client) {
        const scriptLoaded = document?.querySelector('#recaptcha-script');
        if (scriptLoaded) {
          return executeScript(action, recaptchaSiteKey, formFieldValues, resolve);
        }
      }

      // Dynamically load recaptcha just when it's needed
      useHead({
        script: [
          {
            key: 'recaptcha',
            id: 'recaptcha-script',
            src: `${$config.public.googleRecaptchaScript}?render=${unref(recaptchaSiteKey)}`,
            type: 'text/javascript',
            async: true,
            onerror: (error) => {
              $log.error('Error in recaptcha execute():', error);
              resolve({});
            },
            onload: () => {
              return executeScript(action, recaptchaSiteKey, formFieldValues, resolve);
            },
          },
        ],
      });
    });
  },
});
</script>

<style lang="scss" scoped>
.g-recaptcha-notice {
  display: block;
  margin-top: $pad;

  &--no-margin {
    margin: 0;
  }

  &--small-print {
    font-size: map-get($font-sizes, 's');
    line-height: map-get($font-line-heights, 'xs');
  }
}
</style>
